import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { ArrowLinkIcon, ButtonLink, Card, Notice, Skeleton, Text } from "@ficatranquilo/contabilidade.com-ui";

// Dashboard (/), no que o da empresa e o do prestador compartilham (modelo Dashboard
// do DS OneWork): os números em cartões, os blocos com título, os avisos de pendência
// e o "Tudo em dia".

export const DASHBOARD_HEADER = { title: "Dashboard", breadcrumb: [{ page: "Dashboard", route: "/" }] };

/** Número de uma resposta paginada da API; null quando a chamada falhou. */
export function totalOf(settled) {
  if (settled.status !== "fulfilled") return null;
  const result = settled.value;
  if (!result || result.error) return null;
  const total = result.data?.totalRegisters ?? result.data?.count;
  return total == null ? null : Number(total);
}

// O ContentBox da lib cresce (flex: 1): os blocos de cima ficam na altura do conteúdo,
// e só o último ocupa o resto da página
export const FitBox = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.N100};
  color: ${({ theme }) => theme.colors.ACTION};

  svg {
    width: 14px;
  }
`;

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.$gap || "12px"};
`;

const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const BlockHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

// O clique comum navega sem recarregar; com Ctrl, Shift ou o botão do meio, o href abre em outra aba
function useLinkClick(route) {
  const navigate = useNavigate();
  return (event) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    navigate(route);
  };
}

function StatCard({ label, value, link, route }) {
  const onClick = useLinkClick(route);
  // Sem o número (a chamada falhou), o cartão continua levando à tela
  const text = value == null ? "—" : Number(value).toLocaleString("pt-BR");

  return (
    <Card $action={route} onClick={onClick}>
      <Text $text={label} $variant="paragraph_small_medium" $color="N600" />
      <Text $text={text} $variant="h3_bold" $color="N900" />
      <Foot>
        <Text $text={link} $variant="paragraph_small_bold" $color="ACTION" />
        <ArrowLinkIcon />
      </Foot>
    </Card>
  );
}

/** Os números do topo: `items` [{ key, label, value, link, route }], até 3 por linha. */
export function StatCards({ items, loading }) {
  if (loading) {
    return (
      <StatGrid aria-busy="true">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <Skeleton $width="45%" $height="14px" $noFlex />
            <Skeleton $width="25%" $height="28px" $noFlex />
            <Skeleton $width="40%" $height="14px" $noFlex />
          </Card>
        ))}
      </StatGrid>
    );
  }
  return (
    <StatGrid>
      {items.map((item) => (
        <StatCard key={item.key} {...item} />
      ))}
    </StatGrid>
  );
}

/** Bloco com o título em h4_bold e, se houver, um atalho à direita ({ text, route }). */
export function DashboardBlock({ title, action, children }) {
  const navigate = useNavigate();
  return (
    <Block>
      <BlockHead>
        <Text $text={title} $variant="h4_bold" $color="N900" />
        {action ? <ButtonLink $text={action.text} $action={() => navigate(action.route)} $noPadding $noWidth /> : null}
      </BlockHead>
      {children}
    </Block>
  );
}

/** Uma pendência: o número em negrito no texto e o atalho para a tela dela. */
export function PendingNotice({ title, text, action, route }) {
  const navigate = useNavigate();
  return (
    <Notice
      $variant="blue"
      $title={title}
      $text={text}
      $actions={<ButtonLink $text={action} $action={() => navigate(route)} $noPadding $noWidth />}
    />
  );
}

export function AllClear({ text }) {
  return <Notice $variant="green" $title="Tudo em dia" $text={text} />;
}

export function PendingSkeleton() {
  return (
    <Stack aria-busy="true">
      <Skeleton $height="72px" $noFlex />
      <Skeleton $height="72px" $noFlex />
    </Stack>
  );
}
